/**
 * client/src/services/carBridge.ts
 *
 * ANDROID AUTO — sincronizzazione dei percorsi verso il display del veicolo
 * (Ott 2026).
 *
 * PERCHÉ UNO SNAPSHOT: l'esperienza in auto (categoria POI, vedi
 * `client/android/app/src/main/java/app/rideconnect/it/car/`) gira in un
 * processo separato dal WebView e non può chiamare il bridge JS. Il telefono
 * quindi produce un JSON con i percorsi salvati e lo deposita nel plugin
 * nativo `CarBridge`, che lo salva in SharedPreferences; i template
 * dell'auto lo leggono da lì.
 *
 * REGOLE:
 *  - no-op totale su web/desktop (nessun errore, nessun log rumoroso);
 *  - nessuna eccezione propagata: la sincronizzazione è un'ottimizzazione,
 *    non deve mai interferire con registrazione/navigazione in corso;
 *  - il JSON è versionato (`v`) e limitato (`MAX_CAR_ITEMS`) perché arriva in
 *    un template che deve essere costruito in pochi millisecondi.
 */

import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import type { Route } from "@/types";
import { getRoutes } from "@/api/routes/routesApi";

/** Versione del contratto JSON: allineata a `CarSnapshot.VERSION` (nativo). */
export const CAR_SNAPSHOT_VERSION = 4;

/** Numero massimo di voci inviate all'auto (limite nativo: CarSnapshot.MAX_ITEMS). */
export const MAX_CAR_ITEMS = 30;

/**
 * Numero massimo di punti della traccia inviati all'auto (contratto v4).
 *
 * La traccia serve a disegnare il giro in registrazione sul display del
 * veicolo; 150 punti bastano per la forma del percorso e tengono il JSON
 * dello snapshot piccolo (SharedPreferences lette a ogni template).
 */
export const MAX_CAR_TRACK_POINTS = 150;

/**
 * Quanti punti INIZIALI della traccia restano sempre inclusi quando la traccia
 * viene ridotta a {@link MAX_CAR_TRACK_POINTS} (Milestone 1, 13 Ott 2026).
 *
 * La riduzione è a FINESTRA SCORREVOLE: questi punti danno il contesto visivo
 * della partenza, i restanti arrivano dalla coda — gli ultimi punti REALI,
 * così il tratto finale e il pin "Adesso" restano fedeli al percorso.
 */
export const CAR_TRACK_HEAD_POINTS = 20;

/** Lunghezza massima di titolo/sottotitolo (limite nativo: MAX_TEXT_LENGTH). */
export const MAX_CAR_TEXT_LENGTH = 120;

/** Titolo del paywall mostrato in auto senza Premium. */
export const CAR_PAYWALL_TITLE = "RideConnect Premium";

/**
 * Messaggio del paywall: l'accesso in auto è riservato a chi ha un
 * abbonamento attivo o la prova Premium in corso. Nessuna vendita dal
 * display (policy Play): si attiva dal telefono.
 */
export const CAR_PAYWALL_MESSAGE =
  "Passa a Premium (o attiva la prova gratuita) per usare i tuoi percorsi sul " +
  "display della moto. Puoi attivarlo dall'app sul telefono: Profilo → Abbonamento.";

export interface CarSnapshotItem {
  id: string;
  kind: "route";
  title: string;
  subtitle?: string;
  lat: number;
  lng: number;
  /**
   * Punto di PARTENZA del percorso (punto di ritrovo), se valido.
   *
   * CHIAREZZA NAVIGAZIONE (11 Ott 2026, richiesta utente: "fa' confusione con
   * inizio e fine, ovvero Partenza e/o punto di ritrovo e Destinazione"):
   * il tap sulla riga in auto apre la scelta esplicita tra partenza e
   * destinazione, così l'app di navigazione riceve il punto giusto — con un
   * anello (partenza ≈ destinazione) o senza start valido la scelta sparisce
   * e resta solo la destinazione.
   */
  startLat?: number;
  startLng?: number;
  /**
   * Distanza del percorso in km (arrotondata, > 0).
   *
   * L'auto la mostra come "span di distanza" sulla riga: il car host rifiuta
   * le righe senza distanza (`PlaceListMapTemplate`: "All non-browsable rows
   * must have a distance span attached to either its title or texts") e senza
   * questo valore la sessione si chiuderebbe con un errore. Vedi
   * `SavedRoutesScreen.withDistanceSpan`.
   */
  distanceKm?: number;
}

export interface CarSnapshot {
  v: number;
  updatedAt: number;
  /** true se l'utente può usare l'esperienza in auto (abbonato o in prova). */
  premium: boolean;
  userName?: string;
  paywallTitle?: string;
  paywallMessage?: string;
  /**
   * Registrazione in corso sul telefono: l'hub in auto mostra lo stato e i
   * comandi "Ferma e salva" / "Annulla registrazione".
   */
  recording?: boolean;
  /** km percorsi nella registrazione in corso (0 se non attiva). */
  recordingKm?: number;
  /** quando è iniziata la registrazione in corso (epoch ms). */
  recordingStartedAt?: number;
  /**
   * Traccia del giro in registrazione (contratto v4): coppie [lat, lng]
   * campionate, max {@link MAX_CAR_TRACK_POINTS}. È la base per disegnare il
   * percorso sul display del veicolo (Android Auto Fase 2); presente solo
   * con registrazione attiva e almeno 2 punti validi.
   */
  trackPoints?: Array<[number, number]>;
  /**
   * Template URL dei tile stradali (contratto v5): base cartografica della
   * mappa nativa in auto (strade sotto la scia). Assente = mappa scura.
   */
  mapTileUrl?: string;
  items: CarSnapshotItem[];
}

/** Stato della registrazione da mostrare in auto (null = nessuna in corso). */
export interface CarRecordingState {
  startedAt: number;
  distanceKm: number;
  /** Waypoint registrati finora (grezzi): lo snapshot li campiona per l'auto. */
  track?: Array<{ lat: number; lng: number }> | null;
}

/**
 * Template URL dei tile stradali per la mappa nativa in auto (contratto v5).
 *
 * Stadia Maps (tema scuro "alidade_smooth_dark", lo stesso del telefono) quando
 * la chiave `VITE_STADIA_API_KEY` è configurata; altrimenti OpenStreetMap, così
 * la mappa in auto funziona anche senza chiave (stile chiaro). Senza rete il
 * renderer nativo mostra comunque la scia su sfondo scuro.
 */
export function carTileUrlTemplate(stadiaKey?: string | null): string {
  // undefined = usa la chiave configurata nell'app; "" / null = nessuna chiave
  // (richiesta esplicita di OpenStreetMap, usata anche dai test).
  const raw =
    stadiaKey === undefined
      ? import.meta.env?.VITE_STADIA_API_KEY
      : stadiaKey;
  const key = String(raw ?? "").trim();
  if (key) {
    return (
      "https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}.png" +
      `?api_key=${encodeURIComponent(key)}`
    );
  }
  return "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
}

/**
 * Stato della registrazione in corso, condiviso tra la pagina di registrazione
 * e la sincronizzazione verso l'auto.
 *
 * Vive in un modulo (non in React) perché lo snapshot per l'auto è costruito
 * anche fuori dal render: la pagina chiama `setCarRecordingState` a inizio/fine
 * registrazione e `syncCarSnapshotNow()` legge da qui.
 */
let carRecordingState: CarRecordingState | null = null;

export function setCarRecordingState(state: CarRecordingState | null): void {
  carRecordingState = state;
}

export function getCarRecordingState(): CarRecordingState | null {
  return carRecordingState;
}

/**
 * Avvia la "sentinella" nativa della posizione per Android Auto (Milestone 2,
 * 14 Ott 2026).
 *
 * PERCHÉ: lo snapshot del display auto lo scrive la WebView; in background
 * Android la sospende e il pin "Adesso" resta fermo all'ultimo punto inviato
 * (mentre Google disegna la posizione reale del veicolo) — i due si
 * contraddicono. Il relay nativo legge il GPS e tiene aggiornata la
 * posizione usata dal template dell'auto.
 *
 * Best-effort: non lancia mai, ritorna true solo se il relay è attivo.
 */
export async function startCarLocationRelay(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const result = await NativeCarBridge.startLocationRelay();
    return result?.started === true;
  } catch (err) {
    console.warn("[carBridge] relay posizione non avviato:", err);
    return false;
  }
}

/** Ferma la sentinella nativa (fine registrazione, annullamento). Best-effort. */
export async function stopCarLocationRelay(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await NativeCarBridge.stopLocationRelay();
  } catch (err) {
    console.warn("[carBridge] relay posizione non fermato:", err);
  }
}

export interface CarBridgeStatus {
  items: number;
  updatedAt: number;
  projectionInstalled: boolean;
}

// ── TRACCIA NATIVA (16 Ott 2026) ────────────────────────────────────────────
// Il relay nativo persiste OGNI fix GPS su SQLite anche con la WebView
// sospesa: al salvataggio la traccia web viene completata con i punti nativi
// (riempimento dei buchi). Vedi CarLocationRelay/recordAndMergeNativeTrack.

/** Punto GPS persistito dal relay nativo. */
export interface NativeTrackPoint {
  local_id: number;
  session_id?: string;
  lat: number;
  lng: number;
  accuracy?: number | null;
  speed?: number | null;
  timestamp: number;
}

/**
 * Imposta la sessione di registrazione corrente per il relay nativo: da qui
 * in poi ogni fix viene persistito con questo id. Null = pausa persistenza.
 * Best-effort: su web o senza plugin non fa nulla.
 */
export async function setNativeTrackSession(
  sessionId: string | null,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await NativeCarBridge.setTrackSession({ sessionId });
  } catch (err) {
    console.warn("[carBridge] setTrackSession fallito:", err);
  }
}

/**
 * Legge la traccia persistita dal relay nativo per la sessione (o per la
 * sessione corrente se null). Ritorna [] su web o in caso di problema: la
 * registrazione non deve mai dipendere dal nativo.
 */
export async function getNativeTrack(
  sessionId: string | null,
  limit = 20000,
): Promise<NativeTrackPoint[]> {
  if (!Capacitor.isNativePlatform()) return [];
  try {
    const result = await NativeCarBridge.getNativeTrack({ sessionId, limit });
    return Array.isArray(result?.points) ? result.points : [];
  } catch (err) {
    console.warn("[carBridge] getNativeTrack fallito:", err);
    return [];
  }
}

/** Cancella la traccia persistita (fine salvataggio o annullo). Best-effort. */
export async function clearNativeTrack(sessionId: string | null): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await NativeCarBridge.clearNativeTrack({ sessionId });
  } catch (err) {
    console.warn("[carBridge] clearNativeTrack fallito:", err);
  }
}

/**
 * Comando "avvia la registrazione del percorso" toccato sul display del
 * veicolo (schermata `CarRecordScreen` lato nativo).
 */
export const CAR_COMMAND_START_RECORDING = "start-recording";

/** Comando "ferma e salva la registrazione in corso" (dall'hub in auto). */
export const CAR_COMMAND_STOP_AND_SAVE = "stop-and-save";

/** Comando "annulla la registrazione in corso" (scarta la sessione locale). */
export const CAR_COMMAND_DISCARD_RECORDING = "discard-recording";

export interface CarBridgeNativePlugin {
  setSnapshot(options: { json: string }): Promise<{ items: number; updatedAt: number }>;
  clear(): Promise<{ items: number; updatedAt: number }>;
  getStatus(): Promise<CarBridgeStatus>;
  consumeCommand(): Promise<
    CarBridgeStatus & { command?: string; commandName?: string }
  >;
  /**
   * Sentinella nativa della posizione (Milestone 2, 14 Ott 2026): avviata
   * all'inizio della registrazione, aggiorna la posizione mostrata in auto
   * anche quando la WebView è sospesa (telefono in tasca).
   */
  startLocationRelay(): Promise<{ started: boolean }>;
  stopLocationRelay(): Promise<void>;
  /** TRACCIA NATIVA (16 Ott 2026): ciclo di vita della traccia persistita. */
  setTrackSession(options: { sessionId: string | null }): Promise<void>;
  getNativeTrack(options: {
    sessionId: string | null;
    limit?: number;
  }): Promise<{ points: NativeTrackPoint[]; count: number }>;
  clearNativeTrack(options: { sessionId: string | null }): Promise<void>;
  /**
   * Evento "l'auto ha lasciato un comando" (Milestone 1, 13 Ott 2026): il
   * plugin nativo lo emette appena il display scrive un comando, così
   * l'esecuzione non aspetta il polling.
   */
  addListener(
    eventName: "carCommandQueued",
    listenerFunc: () => void,
  ): Promise<PluginListenerHandle>;
}

const NativeCarBridge = registerPlugin<CarBridgeNativePlugin>("CarBridge");

function clampText(value: unknown): string | null {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return null;
  return text.length <= MAX_CAR_TEXT_LENGTH
    ? text
    : `${text.slice(0, MAX_CAR_TEXT_LENGTH - 1).trimEnd()}…`;
}

function isValidCoordinate(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/**
 * true quando due punti sono lo stesso posto per la navigazione (~11 m).
 * Serve a distinguere i giri ad anello (partenza = destinazione) dai percorsi
 * con partenza e arrivo diversi nella scelta mostrata in auto.
 */
export function samePoint(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): boolean {
  return Math.abs(a.lat - b.lat) < 0.0001 && Math.abs(a.lng - b.lng) < 0.0001;
}

/**
 * Campiona la traccia GPS registrata per il display del veicolo (v4).
 *
 * Funzione PURA e deterministica: filtra i punti non validi, arrotonda a
 * ~1 m (5 decimali) e riduce a {@link MAX_CAR_TRACK_POINTS} punti con passo
 * uniforme, tenendo SEMPRE il primo e l'ultimo (partenza e posizione attuale).
 * La forma del percorso resta fedele; il JSON dello snapshot resta piccolo.
 *
 * Ritorna null SOLO quando non c'è alcuna posizione valida. Con UNA SOLA
 * posizione valida ritorna quel punto: l'auto mostra SUBITO la mappa con il
 * segnaposto di partenza e la posizione corrente (richiesta utente 12 Ott
 * 2026: "non mi fa vedere la mappa, forse devo fare qualche km" — prima si
 * aspettava il secondo punto, quindi da fermo la mappa non compariva mai).
 */
export function sampleTrackForCar(
  track: Array<{ lat?: unknown; lng?: unknown }> | null | undefined,
  maxPoints: number = MAX_CAR_TRACK_POINTS,
): Array<[number, number]> | null {
  if (!Array.isArray(track) || track.length === 0) return null;
  if (!Number.isFinite(maxPoints) || maxPoints < 2) return null;

  const round5 = (v: number) => Math.round(v * 100000) / 100000;
  const valid = track.filter((p) =>
    isValidCoordinate(p?.lat, p?.lng),
  ) as Array<{ lat: number; lng: number }>;

  const sampled: Array<[number, number]> = [];
  if (valid.length <= maxPoints) {
    for (const p of valid) sampled.push([round5(p.lat), round5(p.lng)]);
  } else {
    // FINESTRA SCORREVOLE (Milestone 1, 13 Ott 2026): primi punti (la
    // partenza dà il contesto visivo) + coda FEDELE (gli ultimi punti REALI).
    // Il campione uniforme precedente perdeva il tratto finale: il pin
    // "Adesso" e la coda della traccia potevano cadere su punti interpolati.
    const head = Math.min(
      CAR_TRACK_HEAD_POINTS,
      Math.max(2, Math.floor(maxPoints / 2)),
    );
    const tail = maxPoints - head;
    for (let i = 0; i < head; i++) {
      sampled.push([round5(valid[i].lat), round5(valid[i].lng)]);
    }
    for (let i = valid.length - tail; i < valid.length; i++) {
      sampled.push([round5(valid[i].lat), round5(valid[i].lng)]);
    }
  }

  // Consecutivi identici (dopo l'arrotondamento): non aggiungono forma.
  const deduped = sampled.filter(
    (p, i) => i === 0 || p[0] !== sampled[i - 1][0] || p[1] !== sampled[i - 1][1],
  );
  return deduped.length > 0 ? deduped : null;
}

/**
 * Punto di partenza di un percorso: primo waypoint, altrimenti le coordinate
 * `from`/`origin`, altrimenti il primo punto della geometria (i percorsi
 * registrati hanno `geometry` come GeoJSON o come lista di coordinate).
 */
export function routeStartPoint(route: Partial<Route> | null | undefined): {
  lat: number;
  lng: number;
} | null {
  if (!route) return null;

  const waypoints = Array.isArray(route.waypoints) ? route.waypoints : [];
  const first = waypoints[0] as { lat?: unknown; lng?: unknown } | undefined;
  if (first && isValidCoordinate(first.lat, first.lng)) {
    return { lat: first.lat as number, lng: first.lng as number };
  }

  const geometry: any = route.geometry;
  const coords: any[] = Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
  const [lng, lat] = Array.isArray(coords[0]) ? coords[0] : [];
  if (isValidCoordinate(lat, lng)) return { lat, lng };

  const anyRoute = route as any;
  const from = anyRoute.from ?? anyRoute.origin ?? null;
  if (from && isValidCoordinate(from.lat, from.lng)) return { lat: from.lat, lng: from.lng };

  return null;
}

/**
 * Punto di navigazione di un percorso: ULTIMO waypoint valido (la
 * destinazione del giro), altrimenti l'ultimo punto della geometria,
 * altrimenti le coordinate `to`/`destination`, altrimenti — percorsi degeneri
 * a un solo punto — il punto di partenza.
 *
 * FIX NAV-DEST (9 Ott 2026, segnalato dall'utente): prima lo snapshot usava il
 * PRIMO waypoint, quindi toccando una riga in auto la navigazione portava alla
 * PARTENZA del percorso invece che all'arrivo ("mi dà la destinazione iniziale,
 * come destinazione mi dà la partenza").
 */
export function routeDestinationPoint(route: Partial<Route> | null | undefined): {
  lat: number;
  lng: number;
} | null {
  if (!route) return null;

  const waypoints = Array.isArray(route.waypoints) ? route.waypoints : [];
  for (let i = waypoints.length - 1; i >= 0; i--) {
    const wp = waypoints[i] as { lat?: unknown; lng?: unknown } | undefined;
    if (wp && isValidCoordinate(wp.lat, wp.lng)) {
      return { lat: wp.lat as number, lng: wp.lng as number };
    }
  }

  const geometry: any = route.geometry;
  const coords: any[] = Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
  for (let i = coords.length - 1; i >= 0; i--) {
    const [lng, lat] = Array.isArray(coords[i]) ? coords[i] : [];
    if (isValidCoordinate(lat, lng)) return { lat, lng };
  }

  const anyRoute = route as any;
  const to = anyRoute.to ?? anyRoute.destination ?? null;
  if (to && isValidCoordinate(to.lat, to.lng)) return { lat: to.lat, lng: to.lng };

  return routeStartPoint(route);
}

/**
 * Riga di contesto sotto il titolo: il descrittivo del giro + la data.
 *
 * La distanza non è qui: l'auto la disegna da sola come span di distanza
 * accanto al titolo (`distanceKm`), quindi ripeterla la mostrerebbe due volte.
 * Il descrittivo viene saltato quando è già il titolo della riga (`skip`),
 * così la descrizione non appare due volte.
 */
export function buildCarSubtitle(
  route: Partial<Route>,
  options: { locale?: string; skip?: string | null } = {},
): string | undefined {
  const locale = options.locale ?? "it-IT";
  const parts: string[] = [];

  const description = clampText(route.description);
  if (description && description !== (options.skip ?? null)) parts.push(description);

  if (typeof route.created_at === "string" && route.created_at) {
    const date = new Date(route.created_at);
    if (!Number.isNaN(date.getTime())) {
      parts.push(
        new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(date),
      );
    }
  }

  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/** Distanza del percorso in km pronta per l'auto (null se assente/non valida). */
export function routeDistanceKm(route: Partial<Route>): number | null {
  const km = Number(route.distance_km);
  if (!Number.isFinite(km) || km <= 0) return null;
  return Math.round(km);
}

/**
 * Costruisce lo snapshot inviato all'auto.
 *
 * Funzione PURA (nessun accesso a plugin/rete) così è testabile: scarta i
 * percorsi senza punto di destinazione valido (è lì che naviga il tap sulla
 * riga in auto, vedi `routeDestinationPoint`), normalizza i testi e applica il
 * limite di voci. L'ordine di `routes` è preservato (il chiamante passa i
 * percorsi più recenti per primi).
 *
 * ACCESSO PREMIUM: senza abbonamento attivo né prova in corso (`premium`
 * falso) i percorsi NON vengono inclusi nello snapshot — l'auto mostra il
 * messaggio di upgrade. La stessa regola è applicata di nuovo nel parser
 * nativo (`CarSnapshot.parse`), quindi un payload manomesso resta bloccato.
 */
export function buildCarSnapshot(
  routes: Array<Partial<Route>> | null | undefined,
  options: {
    userName?: string | null;
    now?: number;
    max?: number;
    premium?: boolean;
    paywallTitle?: string | null;
    paywallMessage?: string | null;
    /** Registrazione in corso (mostrata nei comandi in auto). */
    recording?: CarRecordingState | null;
    /** Template tile per la base cartografica della mappa nativa (v5). */
    mapTileUrl?: string | null;
  } = {},
): CarSnapshot {
  const max = options.max ?? MAX_CAR_ITEMS;
  const premium = options.premium === true;
  const items: CarSnapshotItem[] = [];

  if (premium) {
    for (const route of routes ?? []) {
      if (items.length >= max) break;
      if (!route) continue;

      const destination = routeDestinationPoint(route);
      if (!destination) continue;

      // Il car host rifiuta le righe senza span di distanza: un percorso senza
      // km utilizzabili non è mostrabile in auto (resta visibile nell'app).
      const distanceKm = routeDistanceKm(route);
      if (distanceKm === null) continue;

      const title = titleFor(route);
      if (!title) continue;

      const item: CarSnapshotItem = {
        id: `route:${String(route.id ?? `${destination.lat},${destination.lng}`)}`,
        kind: "route",
        title,
        distanceKm,
        lat: destination.lat,
        lng: destination.lng,
      };
      // PARTENZA per la scelta di navigazione in auto: si include solo se
      // valida e diversa dalla destinazione (sugli anelli coincidono e la
      // scelta sarebbe un doppione). Epsilon ~11 m: sotto quella distanza i
      // due punti sono lo stesso posto per la navigazione.
      const start = routeStartPoint(route);
      if (start && !samePoint(start, destination)) {
        item.startLat = start.lat;
        item.startLng = start.lng;
      }
      const subtitle = buildCarSubtitle(route, { skip: title });
      if (subtitle) item.subtitle = subtitle;
      items.push(item);
    }
  }

  const snapshot: CarSnapshot = {
    v: CAR_SNAPSHOT_VERSION,
    updatedAt: options.now ?? Date.now(),
    premium,
    items,
  };

  const userName = clampText(options.userName);
  if (userName) snapshot.userName = userName;

  // Stato della registrazione: lo mostra l'hub in auto per offrire
  // "Ferma e salva" / "Annulla" senza toccare il telefono.
  const recording = premium ? options.recording ?? null : null;
  if (recording && Number.isFinite(recording.startedAt) && recording.startedAt > 0) {
    snapshot.recording = true;
    snapshot.recordingStartedAt = Math.round(recording.startedAt);
    const km = Number(recording.distanceKm);
    snapshot.recordingKm = Number.isFinite(km) && km > 0 ? Math.round(km) : 0;
    // Traccia campionata (v4): base per disegnare il giro sul display del
    // veicolo. Assente finché non ci sono almeno 2 punti disegnabili.
    const trackPoints = sampleTrackForCar(recording.track ?? null);
    if (trackPoints) snapshot.trackPoints = trackPoints;
  }

  // Base cartografica della mappa nativa (v5): stradine sotto la scia.
  const tileUrl = options.mapTileUrl;
  if (typeof tileUrl === "string" && tileUrl.includes("{z}")) {
    snapshot.mapTileUrl = tileUrl;
  }

  if (!premium) {
    snapshot.paywallTitle = clampText(options.paywallTitle) ?? CAR_PAYWALL_TITLE;
    snapshot.paywallMessage = clampText(options.paywallMessage) ?? CAR_PAYWALL_MESSAGE;
  }

  return snapshot;
}

/**
 * Titolo della riga mostrata in auto.
 *
 * Preferenza: **nome** del giro → **descrittivo** (il testo con cui l'utente
 * ha salvato la registrazione: è così che riconosce il giro, non dai km) →
 * "Percorso" quando non c'è nessuno dei due. I km NON entrano mai nel titolo:
 * l'auto li disegna accanto, come span di distanza (`distanceKm`).
 */
function titleFor(route: Partial<Route>): string {
  const name = clampText(route.name);
  if (name) return name;
  const description = clampText(route.description);
  if (description) return description;
  return "Percorso";
}

/** true se il ponte nativo è disponibile (app Android nativa, non web). */
export function isCarBridgeAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

/**
 * Invia lo snapshot al plugin nativo.
 * Su web/desktop è un no-op dichiarato (`{ synced: false, reason }`).
 *
 * `premium` deve arrivare dallo stato di abbonamento (abbonato o in prova):
 * quando è falso lo snapshot non contiene percorsi e l'auto mostra l'invito
 * a passare a Premium.
 */
export async function syncCarSnapshot(options: {
  routes?: Array<Partial<Route>> | null;
  userName?: string | null;
  premium?: boolean;
} = {}): Promise<{ synced: boolean; items: number; premium: boolean; reason?: string }> {
  const premium = options.premium === true;
  if (!isCarBridgeAvailable()) {
    return { synced: false, items: 0, premium, reason: "not_native_android" };
  }

  try {
    let routes = options.routes ?? null;
    // I percorsi si scaricano solo per chi ha diritto all'esperienza in auto:
    // nessuna richiesta di rete inutile per gli utenti free.
    if (premium && !routes) {
      const response = await getRoutes();
      routes = response?.routes ?? [];
    }

    const snapshot = buildCarSnapshot(routes, {
      userName: options.userName,
      premium,
      // Stato della registrazione sempre aggiornato (anche quando la sync
      // arriva dal timer): l'hub in auto mostra i comandi di stop/salvataggio.
      recording: getCarRecordingState(),
      // Base cartografica della mappa nativa in auto (strade + scia).
      mapTileUrl: carTileUrlTemplate(),
    });
    const result = await NativeCarBridge.setSnapshot({ json: JSON.stringify(snapshot) });
    return {
      synced: true,
      items: result?.items ?? snapshot.items.length,
      premium,
    };
  } catch (error) {
    // Best-effort: l'esperienza in auto resta con l'ultimo snapshot valido
    console.warn("[carBridge] sincronizzazione con Android Auto non riuscita:", error);
    return { synced: false, items: 0, premium, reason: "sync_failed" };
  }
}

/** Cancella lo snapshot (logout): nessun dato dell'utente in auto. */
export async function clearCarSnapshot(): Promise<void> {
  if (!isCarBridgeAvailable()) return;
  try {
    await NativeCarBridge.clear();
  } catch (error) {
    console.warn("[carBridge] pulizia snapshot Android Auto non riuscita:", error);
  }
}

/**
 * Blocca l'esperienza in auto pur restando loggati (downgrade/scadenza
 * Premium): lo snapshot diventa "paywall" senza percorsi.
 */
export async function lockCarSnapshot(options: { userName?: string | null } = {}): Promise<void> {
  await syncCarSnapshot({ premium: false, routes: [], userName: options.userName });
}

/** Diagnostica: quante voci vede l'auto e se Android Auto è installato. */
export async function getCarBridgeStatus(): Promise<CarBridgeStatus | null> {
  if (!isCarBridgeAvailable()) return null;
  try {
    return await NativeCarBridge.getStatus();
  } catch {
    return null;
  }
}

/** Comando dal display con il suo payload opzionale (nome percorso). */
export interface CarCommandDetail {
  command: string;
  /** Nome digitato dal rider sul display (solo "stop-and-save"), o null. */
  name: string | null;
}

/**
 * Ritira il comando toccato sul display del veicolo con il suo payload.
 *
 * Il consumo è distruttivo lato nativo: una seconda chiamata non restituisce lo
 * stesso comando, quindi la registrazione non può partire due volte. Ritorna
 * `null` su web/desktop, quando non c'è nulla in attesa o se il bridge fallisce
 * (la registrazione dal telefono resta sempre disponibile).
 */
export async function consumeCarCommandWithName(): Promise<CarCommandDetail | null> {
  if (!isCarBridgeAvailable()) return null;
  try {
    const result = await NativeCarBridge.consumeCommand();
    const command = typeof result?.command === "string" ? result.command.trim() : "";
    if (!command) return null;
    const rawName = typeof result?.commandName === "string" ? result.commandName.trim() : "";
    return { command, name: rawName ? rawName : null };
  } catch {
    return null;
  }
}

/**
 * Ascolta l'evento "l'auto ha lasciato un comando" (Milestone 1, 13 Ott 2026).
 *
 * Il plugin nativo lo emette appena il display del veicolo scrive un comando
 * (`CarDataStore.setPendingCommand` → `CarUpdateBus.publishCommandQueued`):
 * la WebView lo drena SUBITO invece di aspettare il polling. Il polling resta
 * il fallback (WebView non viva, evento perso, piattaforma non nativa).
 *
 * Ritorna la funzione per annullare l'ascolto: no-op su web/desktop e se il
 * bridge non supporta l'evento (nessun errore propagato).
 */
export async function addCarCommandQueuedListener(
  callback: () => void,
): Promise<() => void> {
  if (!isCarBridgeAvailable()) return () => {};
  try {
    const handle = await NativeCarBridge.addListener("carCommandQueued", () =>
      callback(),
    );
    return () => {
      void handle.remove();
    };
  } catch (error) {
    console.warn(
      "[carBridge] ascolto dei comandi auto non attivato (resta il polling):",
      error,
    );
    return () => {};
  }
}

/**
 * Variante "solo comando" (compatibilità): scarta il payload e restituisce
 * soltanto la stringa del comando.
 */
export async function consumeCarCommand(): Promise<string | null> {
  const detail = await consumeCarCommandWithName();
  return detail?.command ?? null;
}
